import React, { useCallback } from 'react';
import { useRouter } from 'next/router';
import LinkWrapper from '@src/components/LinkWrapper';
import { HyperLink } from '@src/types/contentful';
import styles from './SubNavMenu.module.scss';
import { camelizeString } from '@src/utils/strings';
import { getAdjacentSubNavItems } from '@src/utils/communityLocations';
import usePreventKeyboardScrolling from '@src/hooks/usePreventKeyboardPageScrolling';

interface SubNavMenu {
  menu: HyperLink[];
  parentItem: HyperLink;
  clickHandler?: () => void;
  isOpen: boolean;
}

interface SubNavMenuItem {
  item: HyperLink;
  parentItem: HyperLink;
  index: number;
  menu: HyperLink[];
  asPath: string;
  clickHandler?: () => void;
}

const SubNavMenuItem: React.FC<SubNavMenuItem> = ({
  menu,
  parentItem,
  item,
  index,
  asPath,
  clickHandler
}) => {
  const adjacentItems = getAdjacentSubNavItems(menu || [], item.title || '');
  const topLevelItemRef = !adjacentItems?.above
    ? camelizeString(parentItem?.title || '')
    : adjacentItems?.above;

  const focusItem = useCallback(
    (e: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (document?.readyState === 'complete') {
        if (e.key === 'ArrowUp') {
          const ref = document?.getElementById(`${topLevelItemRef}`);
          ref?.focus();
        }
        if (e.key === 'ArrowDown') {
          const ref = document?.getElementById(`${adjacentItems?.below}`);
          ref?.focus();
        }
      }
    },
    [adjacentItems?.below, topLevelItemRef]
  );

  return (
    <li
      data-testid="desktopSubNavigationMenuItem"
      className={styles.subNavListItem}
    >
      <LinkWrapper
        id={camelizeString(item.title || '')}
        href={item.urlPath as string}
        hoverUnderline
        isActive={(item.urlPath as string) === asPath}
        openNewWindow={!!item.openNewWindow}
        onClick={clickHandler}
        onKeyUp={(e) => focusItem(e)}
        onKeyDown={(e) => {
          const isArrowUpOrDown = e.key === 'ArrowUp' || e.key === 'ArrowDown';
          if (index === menu.length - 1 && !isArrowUpOrDown) {
            clickHandler?.();
          }
        }}
      >
        {item.title}
      </LinkWrapper>
    </li>
  );
};

const SubNavMenu: React.FC<SubNavMenu> = ({
  menu,
  parentItem,
  clickHandler,
  isOpen
}) => {
  // Disable arrow key page scrolling when menu is open
  usePreventKeyboardScrolling(isOpen || false);

  const { asPath } = useRouter();

  return (
    <div className={styles.subNavMenu} data-testid="desktopSubNavigationMenu">
      <ul className={styles.subNavList}>
        {menu.map((item: HyperLink, index: number) => (
          <SubNavMenuItem
            asPath={asPath}
            key={item.sys?.id}
            parentItem={parentItem}
            item={item}
            index={index}
            menu={menu}
            clickHandler={clickHandler}
          />
        ))}
      </ul>
    </div>
  );
};

export default SubNavMenu;
